import type { ComponentPublicInstance, InjectionKey } from 'vue';
import type { DDrawer } from '#components';
import { computed, inject, provide, ref } from 'vue';

export type DrawerSize = 'md' | 'lg' | 'xl';

export const drawerSizeKey: InjectionKey<(size: DrawerSize) => void> = Symbol('drawerSize');

export const drawerSizeClasses: Record<DrawerSize, string> = {
	md: 'w-160 2xl:w-240',
	lg: 'w-200 2xl:w-280',
	xl: 'w-240 2xl:w-320',
};

export function useDrawer() {
	const drawerRef = ref<ComponentPublicInstance<typeof DDrawer> | null>(null);

	return {
		drawerRef,
		close: () => drawerRef.value?.close(),
	};
}

/**
 * Provide drawer size control from DrawerPage.
 * Returns a reactive size ref and the classes to apply.
 */
export function useProvideDrawerSize() {
	const size = ref<DrawerSize>('md');

	function setSize(newSize: DrawerSize) {
		size.value = newSize;
	}

	provide(drawerSizeKey, setSize);

	const containerClass = computed(() => drawerSizeClasses[size.value]);

	return { size, containerClass };
}

/**
 * Request a different drawer size from within a drawer's content.
 * Returns a setter function, or null if not inside a DrawerPage.
 */
export function useDrawerSize() {
	return inject(drawerSizeKey, null);
}
